import { useState, useEffect } from 'react';
import api from '../../../services/api';
import {
  ContratosPage,
  ContratoForm,
  ContratoPreview,
  ModelosContratoPage,
  ModeloContratoForm,
} from './Contratos';

type View = 
  | 'list' 
  | 'new' 
  | 'edit' 
  | 'preview' 
  | 'modelos' 
  | 'modelo-new' 
  | 'modelo-edit';

interface Client {
  id: string;
  razaoSocial: string;
  nomeFantasia?: string | null;
  cnpj: string;
}

interface ContratoManagementProps {
  initialView?: 'contratos' | 'modelos';
}

export function ContratoManagement({ initialView = 'contratos' }: ContratoManagementProps) {
  const [view, setView] = useState<View>(initialView === 'modelos' ? 'modelos' : 'list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);

  useEffect(() => {
    carregarClientes();
  }, []);

  async function carregarClientes() {
    try {
      const response = await api.get('/admin/clients');
      // A resposta pode ser { data: [...] } ou diretamente um array
      const clientsData = response.data?.data || response.data;
      setClients(Array.isArray(clientsData) ? clientsData : []);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
    }
  }

  // Handlers para Contratos
  function handleNewContrato() {
    setSelectedId(null);
    setView('new');
  }

  function handleEditContrato(id: string) {
    setSelectedId(id);
    setView('edit');
  }

  function handlePreviewContrato(id: string) {
    setSelectedId(id);
    setView('preview');
  }

  function handleBackToList() {
    setSelectedId(null);
    setView('list');
  }

  function handleSaveContrato() {
    setSelectedId(null);
    setView('list');
  }

  // Handlers para Modelos
  function handleNewModelo() {
    setSelectedId(null);
    setView('modelo-new');
  }

  function handleEditModelo(id: string) {
    setSelectedId(id);
    setView('modelo-edit');
  }

  function handleBackToModelos() {
    setSelectedId(null);
    setView('modelos');
  }

  function handleSaveModelo() {
    setSelectedId(null);
    setView('modelos');
  }

  // Renderização baseada na view atual
  switch (view) {
    case 'list':
      return (
        <ContratosPage
          clients={clients}
          onNew={handleNewContrato}
          onEdit={handleEditContrato}
          onPreview={handlePreviewContrato}
        />
      );

    case 'new':
      return (
        <ContratoForm
          clients={clients}
          onBack={handleBackToList}
          onSave={handleSaveContrato}
        />
      );

    case 'edit':
      return (
        <ContratoForm
          contratoId={selectedId!}
          clients={clients}
          onBack={handleBackToList}
          onSave={handleSaveContrato}
        />
      );

    case 'preview':
      return (
        <ContratoPreview
          contratoId={selectedId!}
          onBack={handleBackToList}
        />
      );

    case 'modelos':
      return (
        <ModelosContratoPage
          onNew={handleNewModelo}
          onEdit={handleEditModelo}
        />
      );

    case 'modelo-new':
      return (
        <ModeloContratoForm
          onBack={handleBackToModelos}
          onSave={handleSaveModelo}
        />
      );

    case 'modelo-edit':
      return (
        <ModeloContratoForm
          modeloId={selectedId!}
          onBack={handleBackToModelos}
          onSave={handleSaveModelo}
        />
      );

    default:
      return null;
  }
}
